/**
 * "Terminal Velocity" — original expert speed level (difficulty 9/10).
 * Starts at 2× and never slows down: 3× doubles, a 3× glider corridor, a 4×
 * spike field, and an inverted ceiling run before the final doubles.
 * Authored in block coordinates → standard SSL LevelData.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const column = (bx: number, fromY: number, toY: number): void => {
  for (let by = fromY; by <= toY; by++) block(bx, by);
};
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const trig = (bx: number, props: Record<string, number | string | boolean>): void =>
  push({ type: 'trigger', ...cell(bx, 1), props });

// --- 2× opening (0–56) ----------------------------------------------------------
trig(4, { kind: 'color', channel: 1, hex: '#f97316', duration: 1 });
spike(24);
spike(25);
spike(36);
spike(37);
spike(48);
spike(49);
spike(50);

// --- 3× doubles (56–108) ---------------------------------------------------------
portal(58, 'speed:three');
spike(72);
spike(73);
spike(86);
spike(87);
spike(100);
spike(101);
spike(102);

// --- 3× glider corridor (108–156) ------------------------------------------------
portal(110, 'form:glider');
ceiling(112, 150, 6);
column(120, 0, 1);
column(132, 4, 5);
column(144, 0, 1);
portal(152, 'form:runner', 1);
portal(152, 'form:runner', 4);

// --- 4× spike field (156–216) ------------------------------------------------------
portal(158, 'speed:four');
trig(158.5, { kind: 'shader:shockline', strength: 1, duration: 0.5 });
trig(159, { kind: 'camera:shake', strength: 5, duration: 0.4 });
spike(172);
spike(173);
spike(190);
spike(191);
spike(208);
spike(209);
spike(210);

// --- Inverted ceiling run (216–246) -------------------------------------------------
portal(218, 'gravity:up');
ceiling(220, 244, 6);
spike(228, 5, true);
spike(236, 5, true);
portal(242, 'gravity:down', 5);
portal(242, 'gravity:down', 1);

// --- Final doubles (246–264) ---------------------------------------------------------
spike(252);
spike(253);

// Secret shards on the fast lines.
push({ type: 'collectible', ...cell(42, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(126, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(199, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(264, 2) });

export const TERMINAL_VELOCITY: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Terminal Velocity',
    author: 'Shape Shifters Team',
    description: 'There is no slow lane.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 9,
    tags: ['speed', 'expert', 'official'],
  },
  song: { id: 'voltage', bpm: 150, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'two',
    startForm: 'runner',
    accentColor: '#f97316',
    hazardColor: '#f43f5e',
    backgroundColor: '#190802',
  },
  objects,
};
